import { LoginFormCopy } from "@bluebottlecoffee/design-system";
import {
  AnnouncementBar as AnnouncementBarSchema,
  Footer as FooterSchema,
  Nav as NavSchema,
  TranslationGroupEntry,
} from "../sanity-schema";
import { filters, queries, SharedQueryKey, utils } from "./shared";

export enum QueryKey {
  ACCOUNT_MENU = "accountMenu",
  LOGIN_DIALOG = "loginDialog",
  PAGE_COPY = "pageCopy",
}

type EntryProjectionType = {
  key: string;
  value: string;
};

export type TranslationGroupSanityType = {
  description: string;
  entries: EntryProjectionType[];
  name: string;
};

export interface SanityQueryType {
  [QueryKey.ACCOUNT_MENU]: TranslationGroupSanityType;
  [SharedQueryKey.ANNOUNCEMENT_BAR]: AnnouncementBarSchema;
  [SharedQueryKey.FOOTER]: FooterSchema;
  [QueryKey.LOGIN_DIALOG]: LoginFormCopy;
  [SharedQueryKey.NAV]: NavSchema;
}

const translationGroupProjection = (lang: string) => `{
  description,
  "entries": entries[]{
    key,
    "value": ${utils.getLocalizedString<TranslationGroupEntry>("value", lang)}
  },
  name
}`;

export const buildTranslationGroupQuery = (
  lang: string,
  group: string,
): string => `*[
  ${filters.whereType("translationGroup")} &&
  name == "${group}" &&
  ${filters.excludeDrafts}
][0]${translationGroupProjection(lang)}`;

/**
 * Flattens the array of entries into an object of all the respective keys and
 * values
 *
 * ### Example
 * Converts
 * [{key: someKey, value: someValue}, ...]
 * into
 * {
 *   someKey: someValue,
 *   ...
 * }
 */
export const toCopyObject = <T>(
  lang: string,
  translationGroup: TranslationGroupSanityType,
): T => {
  const copy = {} as T;
  translationGroup.entries.forEach((entry: EntryProjectionType) => {
    copy[entry.key] = entry.value;
  });
  return copy;
};

/** The Sanity query used to generate the account/[x].ts pages */
export const buildAccountSanityQuery = (
  lang: string,
  translationGroupName: string,
): string => `{
  "${QueryKey.ACCOUNT_MENU}": ${buildTranslationGroupQuery(
  lang,
  "AccountMenu",
)},
  "${SharedQueryKey.ANNOUNCEMENT_BAR}": ${queries.announcementBarQuery},
  "${SharedQueryKey.FOOTER}": ${queries.footerQuery},
  "${SharedQueryKey.LOGIN_DIALOG}": ${buildTranslationGroupQuery(
  lang,
  "LoginDialog",
)},
"${SharedQueryKey.CONSENT_MANAGER}": ${buildTranslationGroupQuery(
  lang,
  "SegmentConsentManager",
)},
  "${SharedQueryKey.NAV}": ${queries.navQuery},
  "${QueryKey.PAGE_COPY}": ${buildTranslationGroupQuery(
  lang,
  translationGroupName,
)},
}`;
